import { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { useSession } from './SessionContext';
import { useCalendar } from './CalendarContext';
import { useBranding } from './BrandingContext';
import LeadSalvageModal from '../components/LeadSalvageModal';

/**
 * CIN-6274 lead-salvage decision owner.
 *
 * PROVIDER WIRING: CalendarProvider is a PARENT of SessionProvider, so
 * CalendarContext (where the raw close lives) cannot read session state
 * (personalInfo/currentStep/schedule). This provider is mounted INSIDE
 * SessionProvider (see App.js) so it can read useSession() + useCalendar() +
 * useBranding() and owns the salvage decision. It exposes attemptClose() and
 * renders <LeadSalvageModal/>.
 *
 * Close entry points are re-routed to attemptClose():
 *  - Scheduler.js close-button onClick + handleOverlayClick call attemptClose()
 *    directly via useLeadSalvage().
 *  - CalendarContext.handleBackgroundClick calls a registered interceptor; this
 *    provider registers attemptClose() as that interceptor (registerCloseInterceptor).
 * CalendarContext.closeWidget() remains the RAW close (postMessage CLOSE_WIDGET).
 */
const LeadSalvageContext = createContext();

export const useLeadSalvage = () => {
  const context = useContext(LeadSalvageContext);
  if (!context) {
    throw new Error('useLeadSalvage must be used within a LeadSalvageProvider');
  }
  return context;
};

export const LeadSalvageProvider = ({ children }) => {
  const { calendarId, closeWidget, registerCloseInterceptor } = useCalendar();
  const { sessionId, currentStep, personalInfo, schedule } = useSession();
  const { widgetConfig, branding } = useBranding();

  const [isModalOpen, setIsModalOpen] = useState(false);

  // Once-per-browser-session flag. sessionStorage (cleared on tab close), NOT a
  // cookie, and keyed per calendar (AC One-time #1).
  const flagKey = `scheduler_salvage_shown_${calendarId}`;

  // CONFIGURABILITY SEAM (CIN-6274): the single choke-point that enables/disables
  // the whole feature. A future per-location toggle only needs to change this
  // function. Reads an optional flag from widget/branding config; defaults to
  // ENABLED when unset.
  const isSalvageEnabled = useCallback(() => {
    const flag =
      widgetConfig?.enable_lead_salvage ??
      widgetConfig?.settings?.enable_lead_salvage ??
      branding?.enable_lead_salvage ??
      branding?.settings?.enable_lead_salvage;
    return flag !== false;
  }, [widgetConfig, branding]);

  const hasContactInfo = useCallback(() => {
    const info = personalInfo || {};
    return !!(info.firstName || info.lastName || info.email || info.phoneNumber);
  }, [personalInfo]);

  // SHOW conditions -- ALL must hold.
  const shouldShowSalvage = useCallback(() => {
    if (!isSalvageEnabled()) return false;
    // Need a session to attach the lead to (also guards the load-error path).
    if (!sessionId) return false;
    // (a) booking NOT completed
    if (schedule) return false;
    if (currentStep >= 6) return false; // step 6 == SuccessScreen
    // (b) no contact info collected yet (contact is gathered at step 4)
    if (hasContactInfo()) return false;
    if (currentStep >= 4) return false;
    // (c) not already shown this browser session
    try {
      if (sessionStorage.getItem(flagKey)) return false;
    } catch (e) {
      // sessionStorage unavailable (private mode) -> fall through to raw close
      return false;
    }
    return true;
  }, [
    isSalvageEnabled,
    sessionId,
    schedule,
    currentStep,
    hasContactInfo,
    flagKey,
  ]);

  const markShown = useCallback(() => {
    try {
      sessionStorage.setItem(flagKey, '1');
    } catch (e) {
      // ignore -- sessionStorage unavailable
    }
  }, [flagKey]);

  const attemptClose = useCallback(() => {
    if (shouldShowSalvage()) {
      // Set the flag on FIRST show so dismiss + re-engage in the same session
      // never re-prompts (AC One-time #1).
      markShown();
      setIsModalOpen(true);
    } else {
      closeWidget();
    }
  }, [shouldShowSalvage, markShown, closeWidget]);

  // Register attemptClose as the interceptor CalendarContext.handleBackgroundClick
  // calls; unregister on unmount so the raw close is used if this provider is gone.
  useEffect(() => {
    if (registerCloseInterceptor) {
      registerCloseInterceptor(attemptClose);
      return () => registerCloseInterceptor(null);
    }
  }, [registerCloseInterceptor, attemptClose]);

  // X close and "No Thanks" both dismiss = flag is already set on show + raw
  // close, no submit.
  const handleDismiss = useCallback(() => {
    markShown();
    setIsModalOpen(false);
    closeWidget();
  }, [markShown, closeWidget]);

  const handleSuccess = useCallback(() => {
    setIsModalOpen(false);
    closeWidget();
  }, [closeWidget]);

  const value = { attemptClose, isModalOpen };

  return (
    <LeadSalvageContext.Provider value={value}>
      {children}
      {isModalOpen && (
        <LeadSalvageModal
          calendarId={calendarId}
          sessionId={sessionId}
          onSuccess={handleSuccess}
          onDismiss={handleDismiss}
        />
      )}
    </LeadSalvageContext.Provider>
  );
};
